import { ageFromBirthDate } from 'tio-common/utils/date/age-from-birth-date';
import { and, gt, not, or } from 'tio-ui/utilities';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import { concat, fn, hash } from '@ember/helper';
import Divider from 'tio-reimagined/components/divider';
import { dropTask } from 'ember-concurrency';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { hasInProgressInstanceOfCurrentProgramTemplate } from 'tio-common/utils/tuition-assistance/tas-grouping';
import { initializeTypedProgramRequest } from 'tio-common/requests/tas-program-template';
import { htmlSafe } from '@ember/template';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, HStack, Section, Header } from 'tio-reimagined/components/layout';
import AddDependent from 'tio-employee/components/tuition-assistance/forms/add-dependents';
import Component from '@glimmer/component';
import RadioGroup from 'tio-reimagined/components/radio-group';
import pageTitle from 'ember-page-title/helpers/page-title';
import StartDate from 'tio-employee/components/tas/start-date-eligibility';
import TasFaqs from 'tio-common/components/tas/faqs';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import TioHeader from 'tio-common/components/tio/page/hero/index';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import type ConditionModel from 'tio-data/models/condition';
import type DependentModel from 'tio-data/models/dependent';
import type IntlService from 'ember-intl/services/intl';
import type ProgressBuilder from 'tio-common/services/progress-builder';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasGroupModel from 'tio-data/models/tas-group';
import type TasParticipantModel from 'tio-data/models/tas-participant';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasProgramTemplate from 'tio-data/models/tas-program-template';
import type FeaturesService from 'tio-common/services/features';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import VerticalProgressTracker from 'tio-common/components/tio/vertical-progress-tracker';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import GuidelinesModal from 'tio-employee/components/tas/course-cash/guidelines-modal';
import { PhCaretLeft, PhUsers } from 'ember-phosphor-icons';
import { LinkTo } from '@ember/routing';
import Heading from 'tio-reimagined/components/heading';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import ActivityCard from 'tio-employee/components/reimagined/tas/activity-card';
import {
  applicationAmountInfo,
  getApplicationName,
  getInstanceName,
  getProgramName,
} from 'tio-employee/utils/tas/activity-helpers';
import { isInRepayment } from 'tio-common/utils/tuition-assistance/application-states';
import type {
  RecentApplication,
  ProgramInstance,
} from 'tio-common/utils/tuition-assistance/application-states';

export interface S {
  Args: {
    model: {
      activeInstances: TASProgramInstanceModel[];
      programTemplate: TasProgramTemplate;
      tasParticipant: TasParticipantModel;
      tasGroups: TasGroupModel[];
      recentActivity: {
        flatApplications: RecentApplication[];
        programInstances: ProgramInstance[];
      };
      allApplications: {
        flatApplications: RecentApplication[];
        programInstances: ProgramInstance[];
      };
    };
  };
}

const relationships = [
  { type: 'DependentRelationship.CHILD', name: 'Child' },
  { type: 'DependentRelationship.SPOUSE', name: 'Spouse' },
  { type: 'DependentRelationship.PARTNER', name: 'Partner' },
];

const getDependentRelationshipName = (dependent: DependentModel) => {
  if (!dependent || !dependent.relationship) {
    return 'Unknown';
  }
  return (
    relationships.find((relationship) => relationship.type === dependent.relationship)?.name ??
    'Unknown'
  );
};

export default class AuthenticatedTuitionAssistanceGettingStarted extends Component<S> {
  @service declare intl: IntlService;
  @service declare progressBuilder: ProgressBuilder;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare features: FeaturesService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare betaExperience: BetaExperienceService;

  @tracked conditionToView?: ConditionModel;
  @tracked showTermsAndConditions = false;
  @tracked selectedDependent: string | undefined = undefined;
  @tracked showAllApplications = false;
  @tracked showGuidelinesModal = false;

  get isActiveEligibility() {
    return this.tuitionAssistance.hasActiveEligibilityForProgramTemplate(this.programTemplate);
  }

  get isRestrictedByProgramExclusivity() {
    if (this.args.model.tasGroups.length === 0) {
      return false;
    }

    return hasInProgressInstanceOfCurrentProgramTemplate(
      this.args.model.tasGroups,
      // @ts-expect-error: model types
      this.programTemplate.id,
      this.args.model.activeInstances
    );
  }

  get eligibleBasedOnWaitingPeriod() {
    return !this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
      this.programTemplate.eligibilityWaitingPeriod,
      this.args.model.tasParticipant
    );
  }

  get waitingPeriod() {
    return String(
      getSingleValueForTasField('ELIGIBILITY_WAITING_PERIOD', this.programTemplate.fields) || 0
    );
  }

  get dateEligible() {
    const date = this.tuitionAssistance.waitingPeriodEndDate(
      this.programTemplate.eligibilityWaitingPeriod,
      this.args.model.tasParticipant
    );
    return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get activeProgramInstances() {
    return this.args.model.activeInstances || [];
  }

  get isHeldParticipant() {
    return !!this.args.model.tasParticipant.isHeld;
  }

  get requiredDependentSelectedAndNotEligible() {
    if (!this.isDependentProgram) {
      return false;
    }

    return this.selectedDependent ? this.selectedDependentFields?.eligible : true;
  }

  get selectedDependentFields() {
    const rawAgeLimits = getSingleValueForTasField(
      'DEPENDENT_PROGRAM_AGE_LIMITS',
      this.programTemplate.fields
    ) as Record<string, string>;

    const ageLimitsForDependents = rawAgeLimits || {};

    if (this.activeDependents.length === 0 || !this.selectedDependent) {
      return {
        eligible: false,
        limit: null,
        dependent: null,
      };
    }

    const dependent = this.activeDependents.find(
      (dependent) => dependent.id === this.selectedDependent
    );
    const dependentAge = dependent ? ageFromBirthDate(dependent.birthDate) : 0;

    let relationshipKey = '';
    if (dependent?.relationship && typeof dependent.relationship === 'string') {
      relationshipKey = dependent.relationship.replace('DependentRelationship.', '');
    } else {
      if (this.isDependentProgram) {
        console.warn('Invalid or undefined relationship:', dependent);
      }
    }

    const limit = relationshipKey ? (ageLimitsForDependents[relationshipKey] as string) : null;

    return {
      eligible: limit !== null ? dependentAge > parseInt(limit, 10) : false,
      limit: limit,
      dependent,
    };
  }

  get lowercaseRelationship() {
    const dependent = this.selectedDependentFields.dependent;
    if (!dependent) return '';
    return getDependentRelationshipName(dependent).toLowerCase();
  }

  getDependentIdAsString(dependent: DependentModel): string {
    return dependent.id as string;
  }

  get canApplyToProgram() {
    if (
      this.programTemplate.state !== 'TAS.ProgramTemplateState.LAUNCHED' ||
      this.isHeldParticipant ||
      !this.isActiveEligibility ||
      this.isRestrictedByProgramExclusivity ||
      !this.eligibleBasedOnWaitingPeriod ||
      !this.sessionContext.currentEmployee.id ||
      !this.activeProgramInstances ||
      this.requiredDependentSelectedAndNotEligible
    ) {
      return false;
    }
    if (this.activeProgramInstances.length === 0 || this.isMultipleInstanceProgram) {
      return true;
    }
    const templateIds = this.activeProgramInstances.map(
      (instance: TASProgramInstanceModel) => instance.tasProgramTemplate.id
    );

    const hasActiveInstanceForProgram = templateIds.includes(this.programTemplate.id);
    return !hasActiveInstanceForProgram;
  }

  // Recent Activity computed properties
  get showRecentActivity() {
    const { flatApplications = [], programInstances = [] } = this.args.model.recentActivity || {};

    return flatApplications.length > 0 || programInstances.length > 0;
  }

  get useGroupedView() {
    return this.args.model.programTemplate.isMultipleInstanceProgram;
  }

  get displayedApplications() {
    if (this.showAllApplications) {
      return this.args.model.allApplications || this.args.model.recentActivity;
    }
    return this.args.model.recentActivity;
  }

  get viewToggleText() {
    return this.showAllApplications
      ? 'tas.getting_started.view_recent'
      : 'tas.getting_started.view_all';
  }

  toggleApplicationsView = () => {
    this.showAllApplications = !this.showAllApplications;
  };

  applicationAmountInfo = (app: RecentApplication) => {
    return applicationAmountInfo(app);
  };

  applicationPaymentState = (app: RecentApplication) =>
    isInRepayment(app) ? 'TAS.ApplicationPaymentState.REFUND_REQUESTED' : null;

  get instanceForExistingApp() {
    if (
      this.programTemplate.typeClassification === 'TAS.ProgramType.2' ||
      this.programTemplate.typeClassification === 'TAS.ProgramType.3'
    ) {
      return;
    }
    const templateId = this.programTemplate.id;
    const instances = this.activeProgramInstances;
    const activeInstanceMatchesTemplateId = instances.find(
      (instance: TASProgramInstanceModel) => templateId === instance.tasProgramTemplate.id
    );
    return activeInstanceMatchesTemplateId;
  }

  get programInstance() {
    return this.instanceForExistingApp || {};
  }

  get programTemplate() {
    return this.args.model.programTemplate;
  }

  get hideProcessDescriptionSteps() {
    return this.programTemplate.hideProcessDescriptionSteps;
  }

  get programSteps() {
    return this.progressBuilder.applicationProcess();
  }

  get isMultipleInstanceProgram() {
    return this.programTemplate.isMultipleInstanceProgram;
  }

  get clickHereLinkRoute() {
    // If multiple instance program OR no valid instance ID exists, go to dashboard
    // Otherwise go to the specific program show page
    if (this.isMultipleInstanceProgram || !this.instanceForExistingApp?.id) {
      return 'authenticated.tas.dashboard';
    }
    return 'authenticated.tas.programs.show';
  }

  get clickHereLinkModel(): string | number | undefined {
    // Only return a model ID if we're linking to the show route
    if (
      this.clickHereLinkRoute === 'authenticated.tas.programs.show' &&
      this.instanceForExistingApp?.id
    ) {
      return this.instanceForExistingApp.id;
    }
    return undefined;
  }

  get buttonText() {
    return this.intl.t('common.progress_tracker.start_application');
  }

  get isDependentProgram() {
    return this.programTemplate.isDependentProgram;
  }

  get activeDependents() {
    const dependents = this.args.model.tasParticipant?.employee?.dependents;
    return Array.isArray(dependents)
      ? dependents.filter((dependent: DependentModel) => dependent.notDeleted)
      : [];
  }

  get dependentOptions() {
    return this.activeDependents
      .filter((d: DependentModel) => !!d.id)
      .map((d: DependentModel) => ({
        key: this.getDependentIdAsString(d),
        label: d.fullName,
      }));
  }

  initializeTypedProgram = dropTask(async (route: string, date?: string) => {
    const employeeId = this.sessionContext.currentEmployee.id as string;
    try {
      const response = await this.store.request(
        initializeTypedProgramRequest({
          templateId: this.programTemplate.id as string,
          employeeId,
          eligibilityDate: date ? date : new Date().toLocaleDateString('en-CA'),
          dependentId: this.selectedDependent,
        })
      );
      const newProgram = (response.content as { data: { id: string } }).data;

      const queryParams: Record<string, unknown> = {};
      if (date) {
        queryParams.date = date;
      }
      this.router.transitionTo(route, newProgram.id, { queryParams });
    } catch (e) {
      console.error(e);
    }
  });

  get programTemplateConditions(): readonly ConditionModel[] {
    return Object.freeze([...this.programTemplate.conditions]);
  }

  get programFaqs() {
    return this.programTemplate.programFaqs;
  }

  get requiresDateEligibilityCheck() {
    const programIsTypeTwoOrThree =
      this.programTemplate.typeClassification === 'TAS.ProgramType.2' ||
      this.programTemplate.typeClassification === 'TAS.ProgramType.3';

    const lock = getSingleValueForTasField(
      'LOCK_EMPLOYEE_STATUS_BASED_ON',
      this.programTemplate.fields
    );

    return programIsTypeTwoOrThree && (lock === 'COURSE_START_DATE' || lock === 'COURSE_END_DATE');
  }

  applyToProgram = (date?: string) => {
    const programType = this.programTemplate.typeClassification;
    const suffix = this.betaExperience.isProductEnabled('ta') ? '-guided' : '';
    let route;
    switch (programType) {
      case 'TAS.ProgramType.1':
        route = 'authenticated.tas.programs.new';
        this.initializeTypedProgram.perform(route, date);
        break;
      case 'TAS.ProgramType.2':
        route = `authenticated.tas.applications.type-two${suffix}.new`;
        this.initializeTypedProgram.perform(route, date);
        break;
      case 'TAS.ProgramType.3':
        route = `authenticated.tas.applications.type-three${suffix}.new`;
        this.initializeTypedProgram.perform(route, date);
        break;
      case 'TAS.ProgramType.4':
      case 'TAS.ProgramType.4.2':
      case 'TAS.ProgramType.4.3':
        route = 'authenticated.tas.programs.show';
        this.initializeTypedProgram.perform(route);
        break;
      default:
        console.error('no program type found');
    }
  };

  applyWithoutEligibilityCheck = () => {
    this.applyToProgram();
  };

  setShowConditions = () => {
    this.showTermsAndConditions = !this.showTermsAndConditions;
  };

  close = () => {
    this.conditionToView = undefined;
  };

  goBackToConditionsList = () => {
    this.conditionToView = undefined;
  };

  setConditionToView = (condition: ConditionModel) => {
    this.conditionToView = condition;
  };

  setDependent = (value: string) => {
    this.selectedDependent = value;
  };

  toggleGuidelinesModal = () => {
    this.showGuidelinesModal = !this.showGuidelinesModal;
  };

  <template>
    {{pageTitle
      (concat
        @model.programTemplate.programName " | " (t "tuition_assistance.program_details.default")
      )
    }}
    <VStack>
      <Header>{{t "tuition_assistance.default"}}</Header>
      <TioPageBreadcrumbs class="mb-4" as |b|>
        <b.crumb @route="authenticated.tas.dashboard" @label={{t "dashboard"}} />
        <b.crumb
          @route="authenticated.tas.getting-started"
          @label={{t "tuition_assistance.program_details.default"}}
        />
      </TioPageBreadcrumbs>

      <HStack>
        <VStack class={{if this.isDependentProgram "lg:w-2/3! md:w-1/2!" ""}}>
          {{#if @model.tasParticipant.isHeld}}
            <AlertBanner
              @intent="warning"
              @title={{t "tuition_assistance.held_participant_warnings.dashboard_title"}}
              class="mb-4"
            >
              <p class="text-sm">
                {{t "tuition_assistance.held_participant_warnings.dashboard_description"}}
              </p>
            </AlertBanner>
          {{/if}}

          {{#unless this.isActiveEligibility}}
            <AlertBanner
              @intent="warning"
              @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
              class="mb-4"
            >
              <p class="text-sm">
                {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
              </p>
            </AlertBanner>
          {{/unless}}

          {{#if this.isRestrictedByProgramExclusivity}}
            <AlertBanner
              @intent="warning"
              @title={{t "tuition_assistance.program_exclusivity_warnings.dashboard_title"}}
              class="mb-4"
            >
              <p class="text-sm">
                {{t "tuition_assistance.program_exclusivity_warnings.dashboard_description"}}
              </p>
            </AlertBanner>
          {{/if}}

          {{#if (and @model.tasParticipant.isActive (not this.eligibleBasedOnWaitingPeriod))}}
            <AlertBanner
              @intent="warning"
              @title={{t "tuition_assistance.program_eligibility_warnings.dashboard_title"}}
              class="mb-4"
            >
              <p class="text-sm">
                {{t
                  "tuition_assistance.program_eligibility_warnings.dashboard_description"
                  time=this.waitingPeriod
                  date=this.dateEligible
                  htmlSafe=true
                }}
              </p>
            </AlertBanner>
          {{/if}}

          {{#if this.selectedDependentFields.eligible}}
            <AlertBanner
              @intent="warning"
              @title={{t
                "tuition_assistance.dependent_program_warngings.dashboard_title"
                name=this.selectedDependentFields.dependent.firstName
              }}
              class="mb-4"
            >
              <p class="text-sm">
                {{t
                  "tuition_assistance.dependent_program_warngings.dashboard_description"
                  relationship=this.lowercaseRelationship
                  limit=this.selectedDependentFields.limit
                }}
              </p>
            </AlertBanner>
          {{/if}}
          {{#if
            (and
              @model.programTemplate.isDirectPayProgram
              @model.programTemplate.isSuspended
              @model.programTemplate.suspendedProgramDisclosure
            )
          }}
            <AlertBanner
              @intent="warning"
              @title={{htmlSafe (t "tuition_assistance.alerts.coursecash_program_update")}}
              class="mb-4"
            >
              <p class="text-sm">
                {{@model.programTemplate.suspendedProgramDisclosure}}
              </p>
            </AlertBanner>
          {{/if}}
          <Section>
            <:header>
              <TioHeader
                @headerBeginning={{@model.programTemplate.programName}}
                @description={{@model.programTemplate.programDescription}}
                @template={{@model.programTemplate}}
              />
            </:header>
            <:body>
              {{#if @model.programTemplate.isDirectPayProgram}}
                <CourseCashTag
                  @isDirectPayProgram={{true}}
                  @size="lg"
                  @buttonClass="text-lg no-underline font-semibold"
                  class="text-inherit"
                  @disablePopover={{true}}
                />
                <p class="prose my-2">{{String
                    (getSingleValueForTasField
                      "DIRECT_PAY_DISCLOSURE" @model.programTemplate.fields
                    )
                  }}</p>
                <div>
                  <button
                    type="button"
                    class="tio-anchor text-sm"
                    {{on "click" this.toggleGuidelinesModal}}
                  >
                    {{t "tas.course_cash_guidelines.button_label"}}
                  </button>
                </div>
                <div class="tio-fg-secondary text-xs">
                  <ActionableTranslation
                    @t="tas.card_holder.footer_text"
                    @links={{hash
                      termsOfUse=(hash
                        href="https://stripe.com/legal/issuing/celtic-authorized-user-terms"
                        t="tas.card_holder.footer_text_terms_link"
                        target="_blank"
                        rel="noopener noreferrer"
                      )
                    }}
                  />
                </div>
                <Divider class="mt-6 mb-2" />
              {{/if}}

              {{#if this.requiresDateEligibilityCheck}}
                <StartDate
                  @instanceForExistingApp={{this.instanceForExistingApp}}
                  @isMultipleInstanceProgram={{this.isMultipleInstanceProgram}}
                  @canApplyToProgram={{this.canApplyToProgram}}
                  @buttonText={{this.buttonText}}
                  @applyToProgram={{this.applyToProgram}}
                  @programTemplate={{@model.programTemplate}}
                  @tasParticipant={{@model.tasParticipant}}
                />
              {{else}}
                <div class="my-4">
                  <Button
                    {{on "click" this.applyWithoutEligibilityCheck}}
                    @isDisabled={{not this.canApplyToProgram}}
                    @isRunning={{this.initializeTypedProgram.isRunning}}
                    class="uppercase w-60"
                    @intent="primary"
                  >
                    {{this.buttonText}}
                  </Button>
                </div>
              {{/if}}

              {{#if this.programTemplateConditions}}
                <p class="pt-4 pb-2 font-medium text-xl">
                  {{t "tas.terms_and_conditions"}}</p>
                <Button
                  @appearance="minimal"
                  @intent="primary"
                  {{on "click" this.setShowConditions}}
                >
                  {{htmlSafe (t "tas.terms_you_will_agree_to")}}
                </Button>
              {{/if}}
            </:body>
          </Section>

          {{! Recent Activity Section }}
          {{#if this.showRecentActivity}}
            <Section>
              <:header>
                <div class="flex items-center justify-between">
                  <Header>{{t "tas.getting_started.recent_activity"}}</Header>
                  <button
                    type="button"
                    class="tio-copy text-sm font-semibold"
                    {{on "click" this.toggleApplicationsView}}
                  >
                    {{t this.viewToggleText}}
                  </button>
                </div>
              </:header>
              <:body>
                {{! Recent Activity Display }}
                <div class="grid grid-cols-1 gap-4">
                  {{! Type 1/2/3 Flat Applications }}
                  {{#each this.displayedApplications.flatApplications as |application|}}
                    <ActivityCard
                      @route="authenticated.tas.applications.show"
                      @model={{or application.tasApplicationIdKeyword application.id}}
                      @name={{getApplicationName application}}
                      @state={{application.tasApplicationStateKeyword}}
                      @shortId={{or application.tasApplicationIdKeyword application.id}}
                      @programName={{getProgramName application}}
                      @updatedAt={{or application.updatedAt application.createdAt}}
                      @amountInfo={{this.applicationAmountInfo application}}
                      @paymentState={{this.applicationPaymentState application}}
                    />
                  {{/each}}

                  {{! Type 1 and Type 4.2/4.3 Program Instances - Grouped by instance }}
                  {{#each this.displayedApplications.programInstances as |instance|}}
                    {{#if (gt instance.childApplications.length 0)}}
                      {{! Type 4 instance WITH children — bordered group container }}
                      <div
                        class="rounded-2xl border tio-border-default bg-white shadow-sm overflow-hidden"
                      >
                        {{! Program header row }}
                        <div
                          class="flex items-center justify-between px-4 py-3 bg-gray-50 border-b tio-border-default"
                        >
                          <div class="flex items-center gap-2">
                            <Heading @level={{3}}>
                              {{getInstanceName instance.instanceApplication}}
                            </Heading>
                            <StatePill
                              @state={{instance.instanceApplication.tasProgramInstanceStateKeyword}}
                            />
                          </div>
                          <LinkTo
                            @route="authenticated.tas.programs.show"
                            @model={{or
                              instance.instanceApplication.tasProgramInstanceIdKeyword
                              instance.instanceApplication.id
                            }}
                            class="tio-copy text-sm font-semibold"
                          >
                            {{t "tas.benefit_history.view_program"}}
                          </LinkTo>
                        </div>
                        {{! Child application cards }}
                        <div class="divide-y divide-gray-100">
                          {{#each instance.childApplications as |childApp|}}
                            <ActivityCard
                              @route="authenticated.tas.applications.show"
                              @model={{or childApp.tasApplicationIdKeyword childApp.id}}
                              @name={{getApplicationName childApp}}
                              @state={{childApp.tasApplicationStateKeyword}}
                              @shortId={{or childApp.tasApplicationIdKeyword childApp.id}}
                              @updatedAt={{or childApp.updatedAt childApp.createdAt}}
                              @amountInfo={{this.applicationAmountInfo childApp}}
                              @paymentState={{this.applicationPaymentState childApp}}
                              @variant="nested"
                            />
                          {{/each}}
                        </div>
                      </div>
                    {{else}}
                      {{! Program instance with NO children — standalone graduate card }}
                      <ActivityCard
                        @route="authenticated.tas.programs.show"
                        @model={{or
                          instance.instanceApplication.tasProgramInstanceIdKeyword
                          instance.instanceApplication.id
                        }}
                        @name={{getInstanceName instance.instanceApplication}}
                        @state={{instance.instanceApplication.tasProgramInstanceStateKeyword}}
                        @shortId={{or
                          instance.instanceApplication.tasProgramInstanceIdKeyword
                          instance.instanceApplication.id
                        }}
                        @programName={{getProgramName instance.instanceApplication}}
                        @updatedAt={{or
                          instance.instanceApplication.updatedAt
                          instance.instanceApplication.createdAt
                        }}
                        @institution={{instance.instanceApplication.tasProgramInstanceFieldSchoolInstitutionNameKeyword}}
                        @iconKind="program"
                      />
                    {{/if}}
                  {{/each}}
                </div>
              </:body>
            </Section>
          {{/if}}

          <TasFaqs @template={{@model.programTemplate}} />

          {{#unless this.hideProcessDescriptionSteps}}
            <Section>
              <:header>
                <h2 class="text-sm font-semibold mb-4">
                  {{t "tuition_assistance.program_steps.application_process"}}
                </h2>
              </:header>
              <:body>
                <VerticalProgressTracker @steps={{this.programSteps}} class="-mb-8 mx-4" />
              </:body>
            </Section>
          {{/unless}}
        </VStack>

        {{#if this.isDependentProgram}}
          <VStack class="lg:w-1/3! md:w-1/2!">
            <Section
              @headerClass="flex items-center gap-2 py-2 px-5 text-sm font-medium tracking-wide -m-4"
            >
              <:header>
                <PhUsers @size="32" />
                {{t "common.tuition_assistance.dependent_select"}}
              </:header>
              <:body>
                <RadioGroup
                  data-test-id="dependent-select-radio-group"
                  @value={{this.selectedDependent}}
                  @onChange={{this.setDependent}}
                  @options={{this.dependentOptions}}
                />
                {{#each this.activeDependents as |dependent|}}
                  {{#if dependent.id}}
                    <div class="flex flex-wrap items-center gap-8 py-4">
                      <span>{{dependent.fullName}}</span>
                      <span class="flex grow justify-end gap-8">
                        <Chip @size="sm" @appearance="faded" @intent="default" class="px-2">
                          {{getDependentRelationshipName dependent}}
                        </Chip>
                        {{t "common.tuition_assistance.dependent_age"}}
                        {{ageFromBirthDate dependent.birthDate}}
                      </span>
                    </div>
                    <Divider class="my-2" />
                  {{/if}}
                {{/each}}
                <AddDependent @employee={{@model.tasParticipant.employee}} class="justify-center" />
              </:body>
            </Section>
          </VStack>
        {{/if}}
      </HStack>
    </VStack>

    <Modal
      @isOpen={{this.showTermsAndConditions}}
      @onClose={{this.setShowConditions}}
      class="min-h-64"
    >
      <:header>
        {{#if this.conditionToView}}
          {{! DETAIL VIEW: Back navigation header }}
          <button
            type="button"
            class="text-ocean-600 hover:text-ocean-800 flex items-center gap-2"
            {{on "click" this.goBackToConditionsList}}
            aria-label={{t "common.back"}}
          >
            <PhCaretLeft @size="20" class="shrink-0" />
            <span class="font-semibold">{{t "tas.terms_and_conditions"}}</span>
          </button>
        {{else}}
          {{! LIST VIEW: Simple header }}
          <p class="font-semibold">{{t "tas.terms_and_conditions"}}</p>
        {{/if}}
      </:header>
      <:body>
        {{#if this.conditionToView}}
          {{! DETAIL VIEW: Single condition full text }}
          <p class="font-semibold text-lg">{{this.conditionToView.prompt}}</p>
          <div class="mr-2">
            {{htmlSafe this.conditionToView.fullText}}
          </div>
        {{else}}
          {{! LIST VIEW: All conditions with VIEW buttons }}
          {{#each this.programTemplateConditions as |condition|}}
            <div class="flex justify-between items-center my-2 w-11/12">
              <div class="flex items-center">
                <p class="text-pretty">{{condition.prompt}}</p>
              </div>
              {{#if condition.hasPdf}}
                <a
                  type="button"
                  class="text-ocean-600 font-medium text-sm uppercase ml-2"
                  href={{condition.pdfUrl}}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{t "common.view"}}
                </a>
              {{else}}
                <button
                  type="button"
                  class="text-ocean-600 font-medium text-sm uppercase ml-2"
                  {{on "click" (fn this.setConditionToView condition)}}
                >
                  {{t "common.view"}}
                </button>
              {{/if}}
            </div>
          {{/each}}
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{this.setShowConditions}}
          @submitText={{t "common.close"}}
        />
      </:footer>
    </Modal>

    {{! Guidelines Modal }}
    <GuidelinesModal @isOpen={{this.showGuidelinesModal}} @onClose={{this.toggleGuidelinesModal}} />
  </template>
}
